import React, { useState,useEffect } from "react";
import { Table, Pagination, Select, TextInput, Button, Spinner, Badge } from "flowbite-react";
import { MdOutlinePersonOff } from "react-icons/md";
import { useGetStudentsByCampusQuery } from "../../../../graphql/generated.tsx";
import LightSpinner from "../../../../components/LightSpinner.jsx";
import Tour from "reactour";

const StudentLookupTab = ({ availableClusters, campusId, navigate }) => {
  const [selectedCluster, setSelectedCluster] = useState(availableClusters?.[0]?.id);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [allStudents, setAllStudents] = useState([]);

  const studentsPerPage = 20;

  const {
    data: studentData,
    loading: studentLoading,
    refetch,
  } = useGetStudentsByCampusQuery({
    variables: {
      campusId,
      clusterid: selectedCluster,
      searchTerm: "",
      page: 1,
      limit: 10000, // Fetch all students at once
    },
    fetchPolicy: "cache-and-network",
  });

  // Store all students when data changes
  useEffect(() => {
    if (studentData?.getStudentsByCampus?.students) {
      setAllStudents(studentData.getStudentsByCampus.students);
    }
  }, [studentData]);

  // Refetch when cluster changes
  useEffect(() => {
    setPage(1);
    setSearchTerm("");
    refetch({
      campusId,
      clusterid: selectedCluster,
      searchTerm: "",
      page: 1,
      limit: 10000,
    });
  }, [selectedCluster, campusId, refetch]);

  // Filter students locally based on search term
  const filteredStudents = allStudents.filter((student) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.rollNo?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.department?.toLowerCase().includes(search)
    );
  });

  // Paginate filtered students
  const startIndex = (page - 1) * studentsPerPage;
  const endIndex = startIndex + studentsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);

  const totalStudents = filteredStudents.length;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);

  const highlightMatch = (text) => {
    if (!searchTerm || !text) return text;
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i} className="bg-yellow-200">{part}</mark> : part
    );
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Tour state
  const [isTourOpen, setIsTourOpen] = useState(false);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    
    // Trigger tour after loading completes if permission is true
    if (permissions?.showStudentLookupTour && !studentLoading) {
      setTimeout(() => setIsTourOpen(true), 800);
    }
  }, [studentLoading]);

  const closeTour = () => {
    setIsTourOpen(false);
    const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
    if (permissions) {
      permissions.showStudentLookupTour = false;
      localStorage.setItem("tourPermissions", JSON.stringify(permissions));
    }
    // Clear trigger flag if it exists
    localStorage.removeItem("triggerStudentLookupTour");
  };

  const openTour = () => {
    setIsTourOpen(true);
  };

  const tourSteps = [
    {
      selector: '[data-tour="cluster-select"]',
      content: " Select a batch/cluster to view students enrolled in it.",
    },
    {
      selector: '[data-tour="search-input"]',
      content: " Search by name, roll no, or email to quickly find a student.",
    },
    {
      selector: '[data-tour="student-row"]',
      content: " Click on a student row to view their individual progress and details.",
    },
  ];

  return (
    <div className="p-4 sm:p-10 font - poppins">
      <Tour
        steps={tourSteps}
        isOpen={isTourOpen}
        onRequestClose={(target) => {
          const permissions = JSON.parse(localStorage.getItem("tourPermissions"));
          if (permissions) {
            // If X button clicked, disable all tours
            if (target?.type === 'click' && target?.target?.closest('[data-tour-elem="close"]')) {
              permissions.showStudentLookupTour = false;
              permissions.showStaffReportTour = false;
              permissions.showAllStudentsTour = false;
              permissions.showStaffDashboardTabsTour = false;
             
            } else {
         
              // Otherwise (Got it!), only disable this tour
              permissions.showStudentLookupTour = false;
              permissions.showStaffReportTour = false;
              permissions.showAllStudentsTour = false;
              permissions.showStaffDashboardTabsTour = false;
            }
            localStorage.setItem("tourPermissions", JSON.stringify(permissions));
          }
          setIsTourOpen(false);
        }}
        accentColor="#203c59"
        rounded={12}
        showNumber={false}
        showNavigation={true}
        showButtons={true}
        showCloseButton={true}
       
        lastStepNextButton={
          <button
            style={{
              backgroundColor: "#203c59",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Got it!
          </button>
        }
        nextButton={
          <button
            style={{
              backgroundColor: "#203c59",
              color: "white",
              padding: "8px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Next →
          </button>
        }
        prevButton={
          <button
            style={{
              backgroundColor: "transparent",
              color: "#6b7280",
              padding: "8px 20px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            ← Back
          </button>
        }
        closeWithMask={false}
        disableInteraction={true}
      />
      {/* Top controls */}
      <div className="flex flex-col items-center justify-between gap-2 mb-4 sm:flex-row">
        <div data-tour="cluster-select">
          <Select
            value={selectedCluster}
            onChange={(e) => setSelectedCluster(e.target.value)}
            disabled={availableClusters?.length === 1}
          >
            {availableClusters?.map((cluster) => (
              <option key={cluster.id} value={cluster.id}>
                {cluster.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center w-full sm:w-auto">
          <div data-tour="search-input">
            <TextInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, roll no, or email"
              className="rounded-lg w-[235px]"
            />
          </div>
          {searchTerm && (
            <Button
              color="light"
              className="ml-2"
              onClick={() => setSearchTerm("")}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Loading state */}
      {studentLoading && (
        <div className="flex items-center justify-center h-[500px]">
          <LightSpinner />
        </div>
      )}

      {/* Table */}
      {!studentLoading && paginatedStudents.length > 0 && (
        <div className="p-6 my-6 transition-all bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:p-8 xl:p-10">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-2xl font-bold text-gray-900 dark:text-white">
              Student Dashboard
            </h4>
            <Badge color="info">{totalStudents} Students</Badge>
          </div>
          <p className="mb-6 text-gray-600 dark:text-gray-300">
            Click the student to view individual progress.
          </p>
          <Table className="min-w-full text-center border divide-y divide-gray-200 rounded-lg dark:divide-gray-700 font - poppins">
            <Table.Head className="bg-gray-50 dark:bg-gray-800">
              <Table.HeadCell>Name</Table.HeadCell>
              <Table.HeadCell>Roll No</Table.HeadCell>
              <Table.HeadCell>Department</Table.HeadCell>
              <Table.HeadCell>Email</Table.HeadCell>
            </Table.Head>
            <Table.Body className="bg-white dark:bg-gray-900">
              {paginatedStudents.map((student, idx) => (
                <Table.Row
                  key={student.id}
                  className="transition duration-150 ease-in-out cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-800"
                  onClick={() => navigate(`student/${student.id}`)}
                  data-tour={idx === 0 ? "student-row" : ""}
                >
                  <Table.Cell className="px-4 py-3 text-gray-800 dark:text-gray-200">
                    {highlightMatch(student.name)}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {highlightMatch(student.rollNo)}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {student.department && String(student.department).trim() !== "" ? highlightMatch(student.department) : "-"}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {highlightMatch(student.email)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-4 mt-6">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className={`px-4 py-2 rounded-lg border ${
                  page === 1
                    ? "bg-gray-200 cursor-not-allowed"
                    : "bg-white hover:bg-gray-100"
                }`}
              >
                Prev
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const pageNum = idx + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 rounded-md border ${
                        page === pageNum
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className={`px-4 py-2 rounded-lg border ${
                  page === totalPages
                    ? "bg-gray-200 cursor-not-allowed"
                    : "bg-white hover:bg-gray-100"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!studentLoading && paginatedStudents.length === 0 && (
        <div className="flex flex-col items-center justify-center h-[500px] text-center">
          <h2 className="mb-2 text-2xl font-bold text-gray-600">
            {searchTerm ? 'No matching students found' : 'Oops, No students are enrolled in this batch'}
          </h2>
          <p className="text-gray-500 mb-6 lg:w-[500px]">
            {searchTerm 
              ? `No results for "${searchTerm}". Try a different search term.`
              : 'We couldn\'t find any students. Try selecting a different batch.'}
          </p>
        </div>
      )}
      <button
        onClick={openTour}
        style={{ backgroundColor: "#053859" }}
        className="absolute z-10 flex items-center justify-center w-8 h-8 text-white rounded-full top-4 right-4 hover:bg-opacity-90 focus:ring-4 focus:ring-blue-300"
        aria-label="Help"
      >
        <span className="text-lg font-bold">?</span>
      </button>
    </div>
  );
};

export default StudentLookupTab;
